import MessageTemplateCreationDrawer from '@/components/Drawer/MessageTemplateCreationDrawer'
import {FeatureToggle, isFeatureEnabled} from '@/helpers/featureToggle'
import {useAuth} from '@/hooks/useAuth'
import TabsNav from '@/shared-components/Tabs/TabNav'
import {UserRoleEnumSchema} from '@/types/UserRole'
import React, {Suspense, lazy, useState} from 'react'
import {useIntl} from 'react-intl'
import {useLocation, useParams} from 'react-router-dom'
import {PrimaryButton} from '../shared-components/Button'
import {PageHeader} from '../shared-components/PageHeader'
import {PageContent} from './Template/components/PageContent'
const WhatsAppTemplateTable = lazy(() => import('@/components/MessageTemplateTable/WhatsappTemplateTable'))
const EmailTemplateTable = lazy(() => import('@/components/MessageTemplateTable/EmailTemplateTable'))

export type TemplateTabKey = 'whatsapp' | 'email'

export default function MessageTemplatesList(): React.JSX.Element {
  const {formatMessage} = useIntl()
  const {userRole} = useAuth()
  const canCreateTemplate = userRole === UserRoleEnumSchema.Values.admin
  const onOpenTemplateDrawer = () => {
    setIsOpen(true)
  }
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const onSetIsOpen = (value: boolean) => {
    setIsOpen(value)
  }

  const tabs: Array<{key: TemplateTabKey; label: string}> = [
    ...(isFeatureEnabled(FeatureToggle.whatsAppTemplates)
      ? [{key: 'whatsapp' as const, label: formatMessage({id: 'MESSAGE_TEMPLATE_LIST.WHATSAPP_TEMPLATES_TAB'})}]
      : []),
    ...(isFeatureEnabled(FeatureToggle.emailTemplates)
      ? [{key: 'email' as const, label: formatMessage({id: 'MESSAGE_TEMPLATE_LIST.EMAIL_TEMPLATES_TAB'})}]
      : []),
  ]

  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0)

  const onTabChange = (index: number) => {
    const tab = tabs[index]
    if (tab) {
      setSelectedTabIndex(index)
    }
  }

  const {id: templateId} = useParams()
  const {pathname} = useLocation()
  const isEditing = pathname?.includes('/edit')
  const mode = templateId ? 'details' : 'create'
  const isEventDetailsOpen = Boolean(templateId) || isEditing

  return (
    <PageContent data-testid="message-template-list-container">
      <div className="mb-6">
        <PageHeader title={formatMessage({id: 'MESSAGE_TEMPLATE_LIST.TABLE_HEADER'})} />
        <div className="mt-2 flex items-start gap-4 text-base" data-testid="subtitle-content">
          <div className="subtitle flex-1 text-sm text-text-secondary">
            {formatMessage({id: 'MESSAGE_TEMPLATE_LIST.TABLE_HEADER_SUBTITLE'})}
          </div>
          <div className="button-container ml-auto flex gap-4">
            {canCreateTemplate && tabs[selectedTabIndex]?.key === 'whatsapp' && (
              <PrimaryButton
                iconTrailing="playlist_add"
                size="small"
                data-cy="create-whatsapp-template-button"
                data-testid="create-whatsapp-template-button"
                onClick={onOpenTemplateDrawer}
              >
                {formatMessage({id: 'MESSAGE_TEMPLATE_LIST.CREATE_TEMPLATE_BUTTON'})}
              </PrimaryButton>
            )}
            {canCreateTemplate && tabs[selectedTabIndex]?.key === 'email' && (
              <PrimaryButton
                iconTrailing="upload"
                size="small"
                data-cy="upload-email-template-button"
                data-testid="upload-email-template-button"
                onClick={onOpenTemplateDrawer}
              >
                {formatMessage({id: 'MESSAGE_TEMPLATE_LIST.UPLOAD_EMAIL_TEMPLATE_BUTTON'})}
              </PrimaryButton>
            )}
          </div>
        </div>
      </div>
      <div className="mb-4 ml-1">
        <TabsNav selectedTabIndex={selectedTabIndex} onTabChange={onTabChange} tabs={tabs} />
      </div>
      <Suspense fallback={null}>
        {tabs[selectedTabIndex]?.key === 'whatsapp' && <WhatsAppTemplateTable />}
        {tabs[selectedTabIndex]?.key === 'email' && <EmailTemplateTable />}
      </Suspense>
      <MessageTemplateCreationDrawer
        isOpen={isOpen || isEventDetailsOpen}
        setIsOpen={onSetIsOpen}
        mode={mode}
        templateType={tabs[selectedTabIndex]?.key}
      />
    </PageContent>
  )
}
